import { AppForm } from '@app/components/form/AppForm';
import { EnterToSubmitFocusContainer } from '@app/components/form/EnterToSubmitFocusContainer';
import { FragmentFormActions } from '@app/modules/fragments/screens/add/FragmentFormActions';
import { getFragmentValidation } from '@app/modules/fragments/screens/add/validation';
import { TimeRange } from '@mi-front/core';
import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CreateFragmentBasicFields } from './CreateFragmentBasicFields';
import { CreateFragmentFormEffects } from './CreateFragmentFormEffects';
import { CreateFragmentFormHeader } from './CreateFragmentFormHeader';
import { QuickFragmentExportFields } from './social-export/QuickFragmentExportFields';
import { FragmentsTagInput } from './tags/FragmentsTagInput';
import { CreateFragmentFormValues } from './useFragmentForm';
import { Station } from '@app/core/types/stations';

interface Props {
  station: Station;
  allowExternalExport: boolean;
  initialValues?: Partial<CreateFragmentFormValues>;
  onCreate: (data: CreateFragmentFormValues) => Promise<unknown>;
  onCancel?: () => void;
  onDiscard?: () => void;
  onDraft?: () => void;
  enableCreate?: boolean;
  onChangedValues?: (values: CreateFragmentFormValues) => void;
  /** Unix timestamp range (ms) of the logger stream selection */
  selection?: TimeRange<number> | null;
}

export function CreateFragmentForm({
  station,
  initialValues,
  onCreate,
  onCancel,
  onDiscard,
  onDraft,
  allowExternalExport,
  enableCreate = true,
  onChangedValues,
  selection,
}: Props) {
  const { $t } = useIntl();
  const schema = useMemo(() => getFragmentValidation((msg) => $t(msg)), [$t]);

  return (
    <EnterToSubmitFocusContainer
      enabled={enableCreate}
      focusOnMount={false}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <AppForm<CreateFragmentFormValues>
        schema={schema}
        defaultValues={{
          stationId: station.id,
          title: '',
          description: '',
          tags: [],
          doExternalExport: allowExternalExport,
          platformUpload: {} as CreateFragmentFormValues['platformUpload'],
          ...initialValues,
        }}
        submitEnabled={enableCreate}
        onSuccess={async (data) => {
          await onCreate({
            stationId: data.stationId,
            title: data.title,
            description: data.description,
            tags: data.tags,
            doExternalExport: data.doExternalExport,
            platformUpload: data.platformUpload,
          });
        }}
        FormProps={{
          style: { flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column' },
        }}
      >
        <CreateFragmentFormEffects onChangedValues={onChangedValues} />

        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CreateFragmentFormHeader station={station} selection={selection} />

          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            <Stack spacing={2.5}>
              <CreateFragmentBasicFields />

              <FragmentsTagInput />

              <QuickFragmentExportFields
                station={station}
                durationMs={selection ? selection.to - selection.from : undefined}
                allowExternalExport={allowExternalExport}
              />
            </Stack>
          </Box>

          <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <FragmentFormActions
              saveLabel={$t({ id: 'common.actions.createEntity', defaultMessage: 'Crear' })}
              onCancel={onCancel}
              onDiscard={onDiscard}
              onDraft={onDraft}
              disableSave={!enableCreate}
            />
          </Box>
        </Box>
      </AppForm>
    </EnterToSubmitFocusContainer>
  );
}
